import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Checkbox, Input, Modal, Select } from "antd";
import { NotificationManager } from "react-notifications";
import PurchaseOrderServices from "../../../services/purchaseOrder.service";
import "../../../assets/css/po2-design.css";

const { Option } = Select;

// Task 1891/1892/1893 (Feature 1862 / F4): three-step vendor flow, matching the approved
// prototype (PO-Prototype-v2.html: pickRetailer/pickContact/newRecipient) -
//   1. search the WHOLE retailer list (not just connected ones) and pick a retailer account
//   2. pick or add the specific person at that account ("who are you dealing with")
//   3. or, if the retailer isn't in JewelCloud at all, create a brand new recipient
// Reports the final resolution back to the caller as {retailerDealerId, orderContactId, label}
// - both fields are nullable (a brand new recipient with no stub login has neither a real
// retailer account beyond the stub, nor is required to).
const OrderContactPicker = ({ dealerId, value, onChange }) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";

  const [retailers, setRetailers] = useState([]);
  const [retailersLoading, setRetailersLoading] = useState(false);
  const [retailerModalVisible, setRetailerModalVisible] = useState(false);
  const [retailerSearch, setRetailerSearch] = useState("");
  const retailerSearchDebounceRef = useRef(null);

  const [contactModalVisible, setContactModalVisible] = useState(false);
  const [pendingRetailer, setPendingRetailer] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [newContact, setNewContact] = useState({ name: "", email: "", phone: "" });

  const [savingContact, setSavingContact] = useState(false);

  const [recipientModalVisible, setRecipientModalVisible] = useState(false);
  const [savingRecipient, setSavingRecipient] = useState(false);
  const [recipient, setRecipient] = useState({
    name: "",
    email: "",
    business: "",
    type: "TRADE",
    provisionLogin: true,
    sendConnectionRequest: true,
  });

  // Epic 1857 UI-redesign Phase 9: server-side search-as-you-type against the new lean
  // PO endpoint (SearchRetailersAsync), replacing the old "fetch all 10,000 retailers once,
  // filter client-side" approach - that pattern only existed because the old Dealer-API
  // proc had no real per-request cost benefit from a smaller page anyway (see the proc's
  // own diagnosis). Debounced so typing doesn't fire a request per keystroke.
  const runRetailerSearch = (query) => {
    setRetailersLoading(true);
    PurchaseOrderServices.SearchRetailers(query, 1, 50)
      .then((res) => {
        setRetailersLoading(false);
        setRetailers(res?.data?.status ? res.data.responseData || [] : []);
      })
      .catch(() => setRetailersLoading(false));
  };

  useEffect(() => {
    if (!retailerModalVisible) return;
    runRetailerSearch("");
    // eslint-disable-next-line
  }, [retailerModalVisible]);

  const handleRetailerSearchChange = (value) => {
    setRetailerSearch(value);
    if (retailerSearchDebounceRef.current) clearTimeout(retailerSearchDebounceRef.current);
    retailerSearchDebounceRef.current = setTimeout(() => runRetailerSearch(value), 350);
  };

  const openRetailerModal = () => {
    setRetailerSearch("");
    setRetailerModalVisible(true);
  };

  const openContactModal = (retailer) => {
    setRetailerModalVisible(false);
    setPendingRetailer(retailer);
    setNewContact({ name: "", email: "", phone: "" });
    setContactsLoading(true);
    PurchaseOrderServices.SearchOrderContacts({ retailerDealerId: retailer.dealerId })
      .then((res) => {
        setContactsLoading(false);
        setContacts(res?.data?.status ? res.data.responseData || [] : []);
      })
      .catch(() => setContactsLoading(false));
    setContactModalVisible(true);
  };

  const openRecipientModal = () => {
    setRetailerModalVisible(false);
    setContactModalVisible(false);
    setRecipient({ name: "", email: "", business: "", type: "TRADE", provisionLogin: true, sendConnectionRequest: true });
    setRecipientModalVisible(true);
  };

  const resolve = (retailerDealerId, orderContactId, label) => {
    setContactModalVisible(false);
    setRecipientModalVisible(false);
    onChange({ retailerDealerId: retailerDealerId ?? null, orderContactId: orderContactId ?? null, label });
  };

  const pickExistingContact = (contact) => {
    resolve(pendingRetailer.dealerId, contact.orderContactId, `${pendingRetailer.dealerCompany} · ${contact.fullName}`);
  };

  const skipContact = () => {
    resolve(pendingRetailer.dealerId, null, pendingRetailer.dealerCompany);
  };

  const addContact = () => {
    if (!newContact.name.trim() || savingContact) return;
    setSavingContact(true);
    PurchaseOrderServices.UpsertOrderContact({
      retailerDealerId: pendingRetailer.dealerId,
      fullName: newContact.name.trim(),
      email: newContact.email.trim(),
      phone: newContact.phone.trim(),
    }).then((res) => {
      setSavingContact(false);
      if (res?.data?.status) {
        resolve(pendingRetailer.dealerId, res.data.responseData.orderContactId, `${pendingRetailer.dealerCompany} · ${newContact.name.trim()}`);
      } else {
        NotificationManager.error(t("Could not save the contact."));
      }
    }).catch((err) => {
      setSavingContact(false);
      NotificationManager.error(typeof err?.response?.data === "string" ? err.response.data : t("Could not save the contact."));
    });
  };

  const saveRecipient = () => {
    if (!recipient.name.trim() || savingRecipient) return;
    setSavingRecipient(true);
    PurchaseOrderServices.CreateOrderRecipient({
      fullName: recipient.name.trim(),
      email: recipient.email.trim(),
      businessName: recipient.business.trim(),
      recipientType: recipient.type,
      provisionLogin: recipient.provisionLogin,
      sendConnectionRequest: recipient.sendConnectionRequest,
    }).then((res) => {
      setSavingRecipient(false);
      if (res?.data?.status) {
        const label = recipient.business.trim() || recipient.name.trim();
        resolve(res.data.responseData.retailerDealerId, res.data.responseData.orderContactId, `${label} (${t("new")})`);
      } else {
        NotificationManager.error(t("Could not create the recipient."));
      }
    }).catch((err) => {
      setSavingRecipient(false);
      NotificationManager.error(typeof err?.response?.data === "string" ? err.response.data : t("Could not create the recipient."));
    });
  };

  return (
    <div className="po2-scope" dir={isRtl ? "rtl" : "ltr"} style={{ padding: 0, background: "transparent", border: "none" }}>
      <div className="po2-field">
        <label>{t("Retailer & Contact")}</label>
        <div className="po2-pick po2-on" onClick={openRetailerModal}>
          <div style={{ flex: 1 }}>
            <b>{value?.label || t("Select retailer or add new recipient")}</b>
          </div>
          <span className="po2-btn po2-linky">{t("Change")}</span>
        </div>
      </div>

      <Modal
        title={t("Find retailer")}
        visible={retailerModalVisible}
        onCancel={() => setRetailerModalVisible(false)}
        footer={
          <Button className="po2-btn po2-neutral" onClick={openRecipientModal}>{t("Not in the list - add new recipient")}</Button>
        }
      >
        <div className="po2-scope" dir={isRtl ? "rtl" : "ltr"} style={{ padding: 0, background: "transparent", border: "none" }}>
          <input
            className="po2-in"
            placeholder={t("Search by store name, city or state...")}
            value={retailerSearch}
            onChange={(e) => handleRetailerSearchChange(e.target.value)}
            style={{ marginBottom: 12 }}
            autoFocus
          />
          <p className="po2-hint" style={{ marginBottom: 10 }}>
            {t("Searching the whole GemFind Retailer List - connection status is shown but does not restrict you.")}
          </p>
          <div style={{ maxHeight: 320, overflowY: "auto" }}>
            {retailers.map((r) => (
              <div key={r.dealerId} className="po2-pick" onClick={() => openContactModal(r)}>
                <div style={{ flex: 1 }}>
                  <b>{r.dealerCompany}</b>
                  <span>{r.dealerCity}, {r.dealerState}</span>
                </div>
                <span className={r.isConnected ? "po2-tagok" : "po2-tagwarn"}>
                  {r.isConnected ? t("CONNECTED") : t("NOT CONNECTED")}
                </span>
              </div>
            ))}
            {!retailersLoading && retailers.length === 0 && <div className="po2-empty"><p>{t("No match. Add them as a new recipient instead.")}</p></div>}
          </div>
        </div>
      </Modal>

      <Modal
        title={t("Who are you dealing with?")}
        visible={contactModalVisible}
        onCancel={() => setContactModalVisible(false)}
        footer={[
          <Button key="skip" className="po2-btn po2-neutral" disabled={savingContact} onClick={skipContact}>{t("Skip")}</Button>,
          <Button key="add" className="po2-btn" loading={savingContact} onClick={addContact}>{t("Add contact")}</Button>,
        ]}
      >
        <div className="po2-scope" dir={isRtl ? "rtl" : "ltr"} style={{ padding: 0, background: "transparent", border: "none" }}>
          <p className="po2-hint" style={{ marginBottom: 10 }}>
            {t("Reporting rolls up by business; the document is addressed to the person.")}
          </p>
          {contactsLoading ? (
            <p>{t("Loading...")}</p>
          ) : (
            <div style={{ maxHeight: 200, overflowY: "auto", marginBottom: 12 }}>
              {contacts.map((c) => (
                <div key={c.orderContactId} className="po2-pick" onClick={() => pickExistingContact(c)}>
                  <div style={{ flex: 1 }}>
                    <b>{c.fullName}</b>
                    <span>{c.email} {c.phone ? `· ${c.phone}` : ""}</span>
                  </div>
                  {c.source === "VENDOR_ADDED" && <span className="po2-tagwarn">{t("ADDED AT A SHOW")}</span>}
                </div>
              ))}
              {contacts.length === 0 && <p className="po2-hint">{t("No contacts on file for this account yet.")}</p>}
            </div>
          )}
          <div style={{ borderTop: "1px solid var(--po2-line)", paddingTop: 12 }}>
            <label style={{ fontSize: "var(--po2-t2)", fontWeight: 600, color: "var(--po2-muted)" }}>
              {t("Or add the person standing in front of you")}
            </label>
            <div className="po2-grid po2-g2" style={{ marginTop: 6 }}>
              <input className="po2-in" placeholder={t("Name")} value={newContact.name} onChange={(e) => setNewContact({ ...newContact, name: e.target.value })} />
              <input className="po2-in" placeholder={t("Email")} value={newContact.email} onChange={(e) => setNewContact({ ...newContact, email: e.target.value })} />
            </div>
            <input className="po2-in" placeholder={t("Phone (optional)")} value={newContact.phone} onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })} style={{ marginTop: 8 }} />
            <p className="po2-hint" style={{ marginTop: 6 }}>
              {t("Saved to this retailer's record, selectable at the next show without re-entry.")}
            </p>
          </div>
        </div>
      </Modal>

      <Modal
        title={t("Recipient not yet in JewelCloud")}
        visible={recipientModalVisible}
        onCancel={() => setRecipientModalVisible(false)}
        onOk={saveRecipient}
        confirmLoading={savingRecipient}
        cancelButtonProps={{ disabled: savingRecipient }}
        okText={t("Use this recipient")}
      >
        <div className="po2-scope" dir={isRtl ? "rtl" : "ltr"} style={{ padding: 0, background: "transparent", border: "none" }}>
          <div className="po2-insight">
            <b>{t("No sale is ever blocked by onboarding")}</b>
            {t("A contact record is always created so the order is attributable. A login is optional.")}
          </div>
          <div className="po2-grid po2-g2">
            <div className="po2-field">
              <label>{t("Name")}</label>
              <input className="po2-in" value={recipient.name} onChange={(e) => setRecipient({ ...recipient, name: e.target.value })} placeholder={t("Person at the booth")} />
            </div>
            <div className="po2-field">
              <label>{t("Email")}</label>
              <input className="po2-in" value={recipient.email} onChange={(e) => setRecipient({ ...recipient, email: e.target.value })} placeholder={t("for the document")} />
            </div>
          </div>
          <div className="po2-grid po2-g2" style={{ marginTop: 12 }}>
            <div className="po2-field">
              <label>{t("Business name (optional)")}</label>
              <input className="po2-in" value={recipient.business} onChange={(e) => setRecipient({ ...recipient, business: e.target.value })} />
            </div>
            <div className="po2-field">
              <label>{t("Classification")}</label>
              <Select style={{ width: "100%" }} value={recipient.type} onChange={(val) => setRecipient({ ...recipient, type: val })}>
                <Option value="TRADE">{t("Trade - counts toward show totals")}</Option>
                <Option value="INDIVIDUAL">{t("Individual - reported separately")}</Option>
              </Select>
            </div>
          </div>
          <div style={{ marginTop: 14, paddingTop: 13, borderTop: "1px solid var(--po2-line)" }}>
            <Checkbox
              checked={recipient.provisionLogin}
              onChange={(e) => setRecipient({ ...recipient, provisionLogin: e.target.checked })}
            >
              <b>{t("Provision a JewelCloud login")}</b>
              <div className="po2-hint">{t("Sends an invitation to set a password and view their orders.")}</div>
            </Checkbox>
            <div style={{ marginTop: 8 }}>
              <Checkbox
                checked={recipient.sendConnectionRequest}
                onChange={(e) => setRecipient({ ...recipient, sendConnectionRequest: e.target.checked })}
              >
                <b>{t("Send a connection request in the same action")}</b>
              </Checkbox>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default OrderContactPicker;
